import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ParentSize } from '@visx/responsive';
import { scaleTime, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { LinePath } from '@visx/shape';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { GridColumns } from '@visx/grid';
import { TooltipWithBounds, useTooltip, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import dayjs from 'dayjs';

import { calculateXAxisTickValues, tickFormatFactory } from '../../utils/chartHelpers';
import { DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE } from '../../utils/dates';

import './DeviceDataChart.scss';

const TOOLTIP_STYLES = {
  ...defaultStyles,
  borderRadius: '8px',
  backgroundColor: 'rgba(40, 40, 40, 0.8)',
  color: 'white',
  border: '1px solid #E6E6E6',
  padding: '1rem',
};

// Data accessors for {t, v} format where t = unix timestamp, v = value
export const getX = (point) => point.t;
export const getY = (point) => point.v;

function DeviceDataChart({
  dataSummaries = [],
  chartHeight = 200,
  chartMargin = { top: 50, bottom: 25, left: 50, right: 25 },
  chartStartTime = 0,
  chartEndTime = 0,
}) {
  const { tooltipData, tooltipLeft = 0, tooltipTop = 0, tooltipOpen, showTooltip, hideTooltip } = useTooltip();

  // Calculate start and end times from data if not provided
  const { start, end } = useMemo(() => {
    if (chartStartTime && chartEndTime) {
      return { start: chartStartTime, end: chartEndTime };
    }

    if (dataSummaries.length === 0) {
      // Default to past 2 weeks if no data
      const now = dayjs.tz().startOf('day');
      return {
        start: now.subtract(14, 'day').unix(),
        end: now.unix(),
      };
    }

    const timestamps = dataSummaries.map((d) => d.start_timestamp);
    const minTimestamp = Math.min(...timestamps);
    const maxTimestamp = Math.max(...timestamps);

    return {
      start: minTimestamp,
      end: maxTimestamp,
    };
  }, [dataSummaries, chartStartTime, chartEndTime]);

  // Transform data for upload time chart (convert from seconds to minutes)
  const uploadTimeData = useMemo(() => {
    return dataSummaries
      .map((summary) => ({
        t: summary.start_timestamp,
        v: (summary.avg_time_to_upload || 0) / 60.0, // Convert seconds to minutes
      }))
      .sort((a, b) => a.t - b.t);
  }, [dataSummaries]);

  // Transform data for duration chart (convert to percentage where 3600 seconds = 100%, capped at 100%)
  const durationData = useMemo(() => {
    return dataSummaries
      .map((summary) => ({
        t: summary.start_timestamp,
        v: Math.min(100, ((summary.total_duration || 0) / 3600) * 100),
      }))
      .sort((a, b) => a.t - b.t);
  }, [dataSummaries]);

  const xAxisTicks = calculateXAxisTickValues(start, end);
  const xAxisTickFormat = tickFormatFactory(start, end);

  const formatDateTooltip = useCallback((timestamp) => {
    return dayjs.tz(1000 * timestamp).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE);
  }, []);

  // Chart line configurations for legend
  const chartLines = useMemo(
    () => [
      {
        label: 'Data Coverage (%)',
        colour: '#217BCE',
        strokeDasharray: '5,5',
        legend: true,
      },
      {
        label: 'Upload Delay (min)',
        colour: '#26AF5F',
        strokeDasharray: undefined,
        legend: true,
      },
    ],
    [],
  );

  return (
    <ParentSize debounceTime={10}>
      {(parent) => {
        const xMax = parent.width - chartMargin.left - chartMargin.right;
        const yMax = chartHeight - chartMargin.top - chartMargin.bottom;

        const xScale = scaleTime({
          domain: [start, end],
          range: [0, xMax],
        });

        // Primary Y scale for upload time (minutes)
        const yScale = scaleLinear({
          domain: [0, Math.max(...uploadTimeData.map(getY), 60) * 1.2],
          range: [yMax, 0],
        });

        // Secondary Y scale for data coverage (percentage)
        const yScaleSecondary = scaleLinear({
          domain: [0, Math.max(...durationData.map(getY)) * 1.2],
          range: [yMax, 0],
        });

        const handleTooltip = useCallback(
          (event) => {
            const { x } = localPoint(event) || { x: 0 };
            const x0Unix = xScale.invert(x - chartMargin.left);

            // Find intercepts for upload time line
            const uploadTimeIntercept = (() => {
              if (uploadTimeData.length === 0) return null;

              const bisect = (arr, targetTime) => {
                let low = 0;
                let high = arr.length - 1;

                while (low <= high) {
                  const mid = Math.floor((low + high) / 2);
                  if (arr[mid].t === targetTime) return mid;
                  if (arr[mid].t < targetTime) low = mid + 1;
                  else high = mid - 1;
                }

                if (low >= arr.length) return arr.length - 1;
                if (low === 0) return 0;

                const leftDiff = Math.abs(targetTime - arr[low - 1].t);
                const rightDiff = Math.abs(targetTime - arr[low].t);

                return leftDiff < rightDiff ? low - 1 : low;
              };

              const index = bisect(uploadTimeData, x0Unix);
              const point = uploadTimeData[index];

              if (!point || Math.abs(point.t - x0Unix) > 60 * 60 * 6) return null;

              return {
                label: 'Upload Delay: ',
                value: point.v,
                timestamp: point.t,
                colour: '#26AF5F',
              };
            })();

            // Find intercepts for data coverage line
            const dataCoverageIntercept = (() => {
              if (durationData.length === 0) return null;

              const bisect = (arr, targetTime) => {
                let low = 0;
                let high = arr.length - 1;

                while (low <= high) {
                  const mid = Math.floor((low + high) / 2);
                  if (arr[mid].t === targetTime) return mid;
                  if (arr[mid].t < targetTime) low = mid + 1;
                  else high = mid - 1;
                }

                if (low >= arr.length) return arr.length - 1;
                if (low === 0) return 0;

                const leftDiff = Math.abs(targetTime - arr[low - 1].t);
                const rightDiff = Math.abs(targetTime - arr[low].t);

                return leftDiff < rightDiff ? low - 1 : low;
              };

              const index = bisect(durationData, x0Unix);
              const point = durationData[index];

              if (!point || Math.abs(point.t - x0Unix) > 60 * 60 * 6) return null;

              return {
                label: 'Data Coverage: ',
                value: point.v,
                timestamp: point.t,
                colour: '#217BCE',
              };
            })();

            const lineIntercepts = [dataCoverageIntercept, uploadTimeIntercept].filter(Boolean);

            if (lineIntercepts.length > 0) {
              const formattedDate = formatDateTooltip(uploadTimeIntercept.timestamp);

              showTooltip({
                tooltipData: {
                  lineIntercepts,
                  displayedDate: formattedDate,
                },
                tooltipLeft: x,
                tooltipTop: 80,
              });
            }
          },
          [chartLines, xScale, showTooltip, formatDateTooltip],
        );

        return (
          <div className="DeviceDataChart">
            <svg width={parent.width} height={chartHeight} onMouseMove={handleTooltip} onMouseLeave={hideTooltip}>
              <Group left={chartMargin.left} top={chartMargin.top}>
                <defs>
                  <clipPath id="deviceDataChartBoundary">
                    <rect x={0} y={0} width={xMax} height={yMax} />
                  </clipPath>
                </defs>
                <GridColumns
                  scale={xScale}
                  width={xMax}
                  height={yMax}
                  stroke="#D9DCE1"
                  tickValues={xAxisTicks.slice(1).map((t) => t)}
                />
                <AxisBottom
                  top={yMax}
                  scale={xScale}
                  hideTicks
                  stroke="#D9DCE1"
                  tickFormat={xAxisTickFormat}
                  tickValues={xAxisTicks.map((t) => t)}
                />
                {/* Primary Y-axis for upload time (minutes) */}
                <AxisLeft
                  scale={yScale}
                  hideTicks
                  stroke="#D9DCE1"
                  numTicks={5}
                  tickFormat={(value) => `${value.toFixed(1)}`}
                  tickLabelProps={() => ({
                    dx: -30,
                  })}
                />

                {/* Secondary Y-axis for data coverage (percentage) */}
                <AxisLeft
                  scale={yScaleSecondary}
                  hideTicks
                  stroke="#D9DCE1"
                  numTicks={5}
                  left={xMax + 10}
                  tickFormat={(value) => `${value.toFixed(1)}%`}
                />

                {/* Upload Time line (primary axis) */}
                <LinePath
                  data={uploadTimeData}
                  x={(d) => xScale(getX(d))}
                  y={(d) => yScale(getY(d))}
                  stroke="#26AF5F"
                  strokeWidth={2}
                  clipPath="url(#deviceDataChartBoundary)"
                />

                {/* Data Coverage line (secondary axis) */}
                <LinePath
                  data={durationData}
                  x={(d) => xScale(getX(d))}
                  y={(d) => yScaleSecondary(getY(d))}
                  stroke="#217BCE"
                  strokeWidth={2}
                  strokeDasharray="5,5"
                  clipPath="url(#deviceDataChartBoundary)"
                />

                {
                  // Create the vertical dashed line and circular intercept markers that accompany the tooltip.
                  tooltipOpen && (
                    <g pointerEvents="none" clipPath="url(#deviceDataChartBoundary)">
                      <line
                        stroke="#000"
                        strokeWidth="1"
                        x1={tooltipLeft - chartMargin.left}
                        x2={tooltipLeft - chartMargin.left}
                        y1={0}
                        y2={yMax}
                        strokeDasharray="5,5"
                      />
                      {
                        // Place circular intercept markers for all visible chart lines.
                        tooltipData.lineIntercepts.map(({ value, timestamp, colour, label }, index) => {
                          const x = xScale(timestamp);
                          // Use appropriate scale based on the line type
                          const y = label.includes('Data Coverage') ? yScaleSecondary(value) : yScale(value);
                          return (
                            <circle
                              key={index}
                              className="DeviceDataChart-chartInterceptCircle"
                              cx={x}
                              cy={y}
                              r={7}
                              stroke={colour}
                              strokeWidth="2.5"
                              fill="white"
                            />
                          );
                        })
                      }
                    </g>
                  )
                }
              </Group>
            </svg>

            {
              // Create the chart legend with colour indicator, label, and button to hide or reveal each line chart.
              <div className="DeviceDataChart-legend" style={{ top: chartMargin.top, left: chartMargin.left }}>
                {chartLines
                  .filter(({ legend }) => legend)
                  .map(({ label, colour, strokeDasharray }, index) => {
                    const isUploadTime = label.includes('Upload Delay');
                    const isDataCoverage = label.includes('Data Coverage');
                    return (
                      <div className="DeviceDataChart-legend-item" key={index}>
                        <div className="DeviceDataChart-legend-item-left">
                          <div
                            className="DeviceDataChart-legend-item-left-circle"
                            style={{
                              backgroundColor: colour,
                              backgroundImage: strokeDasharray
                                ? 'repeating-linear-gradient(45deg, transparent, transparent 2px, currentColor 2px, currentColor 4px)'
                                : 'none',
                            }}
                          />
                          <span>{label}</span>
                          {isUploadTime && (
                            <span className="DeviceDataChart-legend-item-left-legend-hint">⬇️ Lower is better</span>
                          )}
                          {isDataCoverage && (
                            <span className="DeviceDataChart-legend-item-left-legend-hint">⬆️ Higher is better</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            }

            {tooltipOpen && tooltipData && (
              <TooltipWithBounds
                key={Math.random()}
                top={tooltipTop}
                left={tooltipLeft + chartMargin.left}
                offsetLeft={35}
                style={TOOLTIP_STYLES}
              >
                <div className="DeviceDataChart-tooltipInfo">
                  <p className="DeviceDataChart-tooltipInfoHeader">{tooltipData.displayedDate}</p>
                  <div className="DeviceDataChart-tooltipInfo-dataholder">
                    {tooltipData.lineIntercepts.map(({ label, value, colour }, index) => (
                      <div key={index} className="DeviceDataChart-tooltipInfo-dataholder-item">
                        <div className="DeviceDataChart-tooltipCircle" style={{ backgroundColor: colour }} />
                        <span className="DeviceDataChart-tooltipInfo-dataholder-text">{label}</span>
                        <span className="DeviceDataChart-tooltipInfo-dataholder-text">
                          {label.includes('Data Coverage') ? `${value.toFixed(1)}%` : `${value.toFixed(1)}min`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </TooltipWithBounds>
            )}
          </div>
        );
      }}
    </ParentSize>
  );
}

DeviceDataChart.propTypes = {
  dataSummaries: PropTypes.arrayOf(
    PropTypes.shape({
      avg_time_to_upload: PropTypes.number,
      total_duration: PropTypes.number,
      start_timestamp: PropTypes.number,
      end_timestamp: PropTypes.number,
    }),
  ),
  chartStartTime: PropTypes.number, // unix timestamp
  chartEndTime: PropTypes.number, // unix timestamp
  chartHeight: PropTypes.number,
  chartMargin: PropTypes.shape({
    top: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
  }),
};

export default DeviceDataChart;
